import { useNavigate } from "react-router-dom";
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { FiChevronDown } from "react-icons/fi";
import { toast } from "sonner";
import { marked } from "marked";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";

import type { User } from "../../../shared/types/user";

import {
  addUser,
  deleteUser,
  fetchUsers,
  updateUser,
} from "../slices/adminSlice";
import { fetchOrdersByUserId } from "../slices/adminOrderSlice";

import {
  fetchSubscribers,
  sendAnnouncement,
  addSubscriber,
} from "../slices/subscriberSlice";

import UserDetailsModal from "../components/UserDetailsModal";
import ActionConfirmationModal from "../components/ActionConfirmationModal";
import SendAnnouncementModal from "../components/SendAnnouncementModal";

import LoadingOverlay from "../../../shared/components/common/LoadingOverlay";

import useMediaQuery from "@mui/material/useMediaQuery";

interface NewUserFormData {
  name: string;
  email: string;
  password: string;
  role: User["role"];
}

const UserManagement = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");

  const { user } = useAppSelector((state) => state.auth);
  const { users, loading, error } = useAppSelector((state) => state.admin);
  const { userOrders, userOrdersLoading, userOrdersError } = useAppSelector(
    (state) => state.adminOrders,
  );
  const {
    subscribers,
    error: subscribersError,
    sendingAnnouncement,
  } = useAppSelector((state) => state.subscribers);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }

    dispatch(fetchUsers());
    dispatch(fetchSubscribers());
  }, [dispatch, navigate, user]);

  const [formData, setFormData] = useState<NewUserFormData>({
    name: "",
    email: "",
    password: "",
    role: "customer",
  });

  const [subscriberEmail, setSubscriberEmail] = useState<string>("");
  const [isAddSubscriberOpen, setIsAddSubscriberOpen] =
    useState<boolean>(false);

  const [isAddUserOpen, setIsAddUserOpen] = useState<boolean>(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [userToView, setUserToView] = useState<User | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] =
    useState<boolean>(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(addUser(formData));
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "customer",
    });
  };

  const handleRoleChange = (userId: string, newRole: User["role"]) => {
    dispatch(updateUser({ id: userId, role: newRole }));
  };

  const openDeleteModal = (u: User) => {
    setUserToDelete(u);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (loading) return;
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await dispatch(deleteUser({ id: userToDelete._id })).unwrap();
      closeDeleteModal();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddSubscriber = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await dispatch(addSubscriber({ email: subscriberEmail })).unwrap();
      setSubscriberEmail("");
      await dispatch(fetchSubscribers()).unwrap();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add subscriber");
    }
  };

  const handleSendAnnouncement = async (subject: string, content: string) => {
    try {
      const html = await marked(content);

      const result = await dispatch(
        sendAnnouncement({
          subject,
          text: content,
          html,
        }),
      ).unwrap();

      toast.success(
        `Announcement sent successfully! ${result.successful}/${result.totalSubscribers} emails delivered.`,
      );
      setIsAnnouncementModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to send announcement. Please try again.");
    }
  };

  const showLoading = userOrdersLoading || loading;

  const MobileSubscriberCard = ({
    subscriber,
  }: {
    subscriber: (typeof subscribers)[number];
  }) => {
    return (
      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <p className="text-xs text-gray-500">Email</p>
        <p className="font-semibold text-gray-900 break-all">
          {subscriber.email}
        </p>

        <p className="mt-3 text-xs text-gray-500">Subscribed At</p>
        <p className="text-sm text-gray-700">
          {new Date(subscriber.subscribedAt).toLocaleString()}
        </p>
      </div>
    );
  };

  const MobileUserCard = ({ u }: { u: User }) => {
    return (
      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Name</p>
            <p className="font-semibold text-gray-900 truncate">{u.name}</p>

            <p className="mt-2 text-xs text-gray-500">Email</p>
            <p className="text-sm text-gray-700 break-all">{u.email}</p>
          </div>

          <div className="shrink-0">
            <p className="text-xs text-gray-500 mb-1 text-right">Role</p>
            <select
              value={u.role}
              onChange={(e) =>
                handleRoleChange(u._id, e.target.value as User["role"])
              }
              className="p-2 border rounded text-sm"
            >
              <option value="customer">Customer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={async () => {
              setUserToView(u);
              dispatch(fetchOrdersByUserId({ userId: u._id }));
              setIsDetailsOpen(true);
            }}
            className="px-4 py-2 rounded-md text-sm font-medium bg-acloblue text-white hover:bg-acloblue/80 hover:cursor-pointer"
          >
            View
          </button>

          <button
            onClick={() => openDeleteModal(u)}
            className="px-4 py-2 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-600 hover:cursor-pointer"
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">User Management</h2>
      <LoadingOverlay show={showLoading} />

      {/* Subscribers Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-800">Subscribers</h3>
          <button
            onClick={() => setIsAnnouncementModalOpen(true)}
            className="bg-acloblue text-white px-6 py-2 rounded hover:bg-acloblue/90 transition hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={subscribers.length === 0}
          >
            Send Announcement
          </button>
        </div>

        {subscribersError && (
          <p className="text-red-500 mb-4">Error: {subscribersError}</p>
        )}

        {/* collapsible Add New Subscriber section */}
        <div className="mb-6 rounded-lg overflow-hidden">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setIsAddSubscriberOpen((v) => !v)}
            className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer select-none"
          >
            <h3 className="text-lg font-bold">Add New Subscriber</h3>
            <FiChevronDown
              className={[
                "text-gray-600 transition-transform duration-300",
                isAddSubscriberOpen ? "rotate-180" : "rotate-0",
              ].join(" ")}
              size={18}
            />
          </div>
          <div
            id="add-subscriber-panel"
            className={[
              "transition-all duration-300 ease-in-out",
              "overflow-hidden",
              isAddSubscriberOpen
                ? "max-h-40 opacity-100"
                : "max-h-0 opacity-0",
            ].join(" ")}
          >
            <div className="p-4">
              <form onSubmit={handleAddSubscriber} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={subscriberEmail}
                    onChange={(e) => setSubscriberEmail(e.target.value)}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 hover:cursor-pointer"
                  >
                    Add Subscriber
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {isMobile ? (
          <div className="space-y-3">
            {subscribers.length === 0 ? (
              <p className="text-center text-gray-500 py-6">
                No subscribers yet
              </p>
            ) : (
              subscribers.map((subscriber) => (
                <MobileSubscriberCard
                  key={subscriber._id}
                  subscriber={subscriber}
                />
              ))
            )}
          </div>
        ) : (
          <div className="overflow-x-auto shadow-md sm:rounded-lg">
            <table className="min-w-full text-left text-gray-500">
              <thead className="bg-gray-100 text-xs uppercase text-gray-700">
                <tr>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Subscribed At</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-center text-gray-500">
                      No subscribers yet
                    </td>
                  </tr>
                ) : (
                  subscribers.map((subscriber) => (
                    <tr
                      key={subscriber._id}
                      className="border-b hover:bg-gray-50"
                    >
                      <td className="p-4 font-medium text-gray-900">
                        {subscriber.email}
                      </td>
                      <td className="p-4">
                        {new Date(subscriber.subscribedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Users Section */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Users</h3>
        {error && <p className="text-red-500 mb-4">Error: {error}</p>}

        {/* collapsible Add New User section */}
        <div className="mb-6 rounded-lg overflow-hidden">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setIsAddUserOpen((v) => !v)}
            className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer select-none"
          >
            <h3 className="text-lg font-bold">Add New User</h3>
            <FiChevronDown
              className={[
                "text-gray-600 transition-transform duration-300",
                isAddUserOpen ? "rotate-180" : "rotate-0",
              ].join(" ")}
              size={18}
            />
          </div>

          <div
            id="add-user-panel"
            className={[
              "transition-all duration-300 ease-in-out",
              "overflow-hidden",
              isAddUserOpen ? "max-h-130 opacity-100" : "max-h-0 opacity-0",
            ].join(" ")}
          >
            <div className="p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-2">Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2">Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="customer">Customer</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 hover:cursor-pointer"
                  >
                    Add User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* User list management */}
        {isMobile ? (
          <div className="space-y-3">
            {users.length === 0 ? (
              <p className="text-center text-gray-500 py-6">No users found</p>
            ) : (
              users.map((u) => <MobileUserCard key={u._id} u={u} />)
            )}
          </div>
        ) : (
          <div className="overflow-x-auto shadow-md sm:rounded-lg">
            <table className="min-w-full text-left text-gray-500">
              <thead className="bg-gray-100 text-xs uppercase text-gray-700">
                <tr>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id} className="border-b hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-900 whitespace-nowrap">
                      {u.name}
                    </td>
                    <td className="p-4">{u.email}</td>
                    <td className="p-4">
                      <select
                        value={u.role}
                        onChange={(e) =>
                          handleRoleChange(
                            u._id,
                            e.target.value as User["role"],
                          )
                        }
                        className="p-2 border rounded"
                      >
                        <option value="customer">Customer</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="p-4 flex gap-2">
                      <button
                        onClick={async () => {
                          setUserToView(u);
                          dispatch(fetchOrdersByUserId({ userId: u._id }));
                          setIsDetailsOpen(true);
                        }}
                        className="bg-acloblue text-white px-4 py-2 rounded hover:bg-acloblue/80 hover:cursor-pointer"
                      >
                        View
                      </button>
                      <button
                        onClick={() => openDeleteModal(u)}
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 hover:cursor-pointer"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isDetailsOpen && userToView && (
        <UserDetailsModal
          user={userToView}
          orders={userOrders}
          onClose={() => {
            setIsDetailsOpen(false);
            setUserToView(null);
          }}
          loading={userOrdersLoading}
          error={userOrdersError}
        />
      )}

      {isDeleteModalOpen && userToDelete && (
        <ActionConfirmationModal
          onClose={closeDeleteModal}
          onConfirm={confirmDeleteUser}
          loading={loading}
          title="Delete user?"
          message={`Are you sure you want to delete **${userToDelete.name}** (${userToDelete.email})?\n**This action cannot be undone.**`}
          confirmText="Yes, delete"
          cancelText="Cancel"
        />
      )}

      {isAnnouncementModalOpen && (
        <SendAnnouncementModal
          onClose={() => setIsAnnouncementModalOpen(false)}
          onSend={handleSendAnnouncement}
          loading={sendingAnnouncement}
        />
      )}
    </div>
  );
};

export default UserManagement;
